import {
  CloudOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import {
  Button,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CreateTenantForm from "../components/CreateTenantForm";
import EditTenantForm from "../components/EditTenantForm";
import {
  CreateTenantDTO,
  Status,
  TenantRecord,
  tenantService,
  UpdateTenantDTO,
} from "../service";

const { Title } = Typography;

const StyledContent = styled.div`
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  min-height: 280px;
`;

const Tenants: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantRecord | null>(
    null,
  );
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getTenantList();
      setTenants(data);
    } catch (error) {
      message.error("Failed to fetch tenants");
      console.error("Error fetching tenants:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreateClick = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleReset = () => {
    // Form reset is handled by the form component
  };

  const handleCreate = async (values: CreateTenantDTO) => {
    try {
      setSubmitting(true);
      const tenantData = {
        ...values,
      };

      const newTenant = await tenantService.createTenant(tenantData);
      setTenants((prev) => [...prev, newTenant]);
      message.success("Tenant created successfully");
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to create tenant");
      console.error("Error creating tenant:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = (record: TenantRecord) => {
    setSelectedTenant(record);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record: TenantRecord) => {
    setSelectedTenant(record);
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async (values: UpdateTenantDTO) => {
    if (!selectedTenant) return;

    try {
      setSubmitting(true);
      // Format the date if it's a dayjs object
      const formattedValues = {
        ...values,
        valid_until: values.valid_until,
      };
      const updatedTenant = await tenantService.updateTenant(
        selectedTenant.id,
        formattedValues,
      );
      setTenants((prev) =>
        prev.map((tenant) =>
          tenant.id === selectedTenant.id ? updatedTenant : tenant,
        ),
      );
      message.success("Tenant updated successfully");
      setIsEditModalVisible(false);
    } catch (error) {
      message.error("Failed to update tenant");
      console.error("Error updating tenant:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record: TenantRecord) => {
    try {
      // TODO: Implement delete tenant API call
      await tenantService.deleteTenant(record.id);
      message.success("Tenant deleted successfully");
      fetchTenants(); // Refresh the list
    } catch (error) {
      message.error("Failed to delete tenant");
      console.error("Error deleting tenant:", error);
    }
  };

  const handleStatusChange = async (record: TenantRecord, checked: boolean) => {
    try {
      const newStatus: Status = checked ? "active" : "inactive";
      await tenantService.updateTenant(record.id, { status: newStatus });
      message.success(`Tenant status updated to ${newStatus}`);
      // Update the local state
      setTenants((prev) =>
        prev.map((tenant) =>
          tenant.id === record.id ? { ...tenant, status: newStatus } : tenant,
        ),
      );
    } catch (error) {
      message.error("Failed to update tenant status");
      console.error("Error updating tenant status:", error);
    }
  };

  const columns: ColumnsType<TenantRecord> = [
    {
      title: "Tenant Name",
      dataIndex: "tenant_name",
      key: "tenant_name",
      sorter: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
    },
    {
      title: "Tenant Code",
      dataIndex: "tenant_code",
      key: "tenant_code",
      sorter: (a, b) => a.tenant_code.localeCompare(b.tenant_code),
    },
    {
      title: "Max Users",
      dataIndex: "max_users",
      key: "max_users",
      sorter: (a, b) => a.max_users - b.max_users,
    },
    {
      title: "Valid Until",
      dataIndex: "valid_until",
      key: "valid_until",
      render: (date: Date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) =>
        new Date(a.valid_until).getTime() - new Date(b.valid_until).getTime(),
    },
    {
      title: "Model Provider",
      key: "model_provider",
      dataIndex: "model_provider",
      render: (provider: string) => (
        <Space>
          {provider === "genbi" && (
            <Tooltip title="GenBI">
              <RobotOutlined style={{ color: "#1890ff" }} />
            </Tooltip>
          )}
          {provider === "openai" && (
            <Tooltip title="OpenAI">
              <RobotOutlined style={{ color: "#52c41a" }} />
            </Tooltip>
          )}
          {provider === "aws" && (
            <Tooltip title="AWS">
              <CloudOutlined style={{ color: "#ff7a00" }} />
            </Tooltip>
          )}
          {provider === "deepseek" && (
            <Tooltip title="DeepSeek">
              <RobotOutlined style={{ color: "#722ed1" }} />
            </Tooltip>
          )}
          <span style={{ marginLeft: 8, textTransform: "capitalize" }}>
            {provider}
          </span>
        </Space>
      ),
      sorter: (a, b) => a.model_provider.localeCompare(b.model_provider),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: TenantRecord["status"], record: TenantRecord) => (
        <Space>
          <Switch
            checked={status === "active"}
            onChange={(checked) => handleStatusChange(record, checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Space>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: Date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Operations",
      key: "operations",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Tenant">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Tenant">
            <Popconfirm
              title="Delete Tenant"
              description="Are you sure you want to delete this tenant?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <StyledContent>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Tenants Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateClick}
        >
          Create Tenant
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tenants}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} tenants`,
        }}
      />

      <Modal
        title="Create New Tenant"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <CreateTenantForm
          onSubmit={handleCreate}
          onCancel={handleCancel}
          onReset={handleReset}
          submitting={submitting}
        />
      </Modal>

      <Modal
        title="Tenant Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTenant && (
          <EditTenantForm
            tenant={selectedTenant}
            onCancel={() => setIsViewModalVisible(false)}
            readonly={true}
          />
        )}
      </Modal>

      <Modal
        title="Edit Tenant"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTenant && (
          <EditTenantForm
            tenant={selectedTenant}
            onSubmit={handleEditSubmit}
            onCancel={() => setIsEditModalVisible(false)}
            submitting={submitting}
          />
        )}
      </Modal>
    </StyledContent>
  );
};

export default Tenants;
